import { useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';

import { Icon } from '~/components';
import { DataUnavailable } from '~/components/DataUnavailable';
import { ScrollCollapseProvider } from '~/components/ScrollCollapse/contexts/ScrollCollapseProvider';
import { Spinner } from '~/components/Spinner';
import { ERROR_FORM_LINK } from '~/modules/artist-roster/constants';
import { useArtistDetailsQuery } from '~/modules/artist-roster/hooks/useArtistDetailsQuery';
import { BREADCRUMBS_PROPS } from '~/modules/constants';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';
import { Breadcrumbs } from '~/modules/navigation/components/Breadcrumbs';

export function ArtistDetailsPage() {
  const { t } = useTranslation('roster');
  const { partyId } = useParams<{ partyId: string }>();
  const { trackEvent } = useAnalytics('Artist Roster');
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    data: artistData,
    error,
    isLoading: isLoadingArtist,
  } = useArtistDetailsQuery({ partyId });

  useEffect(() => {
    if (artistData) {
      trackEvent('artistView', {
        artist_id: artistData.partyId,
        artist_name: artistData.name,
      });
    }
  }, [trackEvent, artistData]);

  const scrollUpCallback = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (!partyId || isNaN(Number(partyId))) {
    return (
      <div className="flex w-full items-center justify-start p-4xl">
        <p className="m-0 font-header text-display-xl leading-none text-default">
          {t('errors.invalidArtistId')}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-xl">
        <Breadcrumbs.Root {...BREADCRUMBS_PROPS.ROOT}>
          <Breadcrumbs.Item to="/roster" {...BREADCRUMBS_PROPS.ITEM_LINK}>
            {t('header.heading')}
          </Breadcrumbs.Item>
          <Breadcrumbs.Item>{partyId}</Breadcrumbs.Item>
        </Breadcrumbs.Root>
        <DataUnavailable
          title={t('errors.loadingArtist.title')}
          subtitle={
            <div className="text-center">
              <Trans
                t={t}
                i18nKey="errors.loadingArtist.subtitle"
                values={{
                  artistName: artistData?.name,
                  partyId,
                }}
                components={{
                  a: (
                    <a
                      href={ERROR_FORM_LINK}
                      className="underline"
                      target="_blank"
                      aria-label={t('errors.loadingArtist.aria')}
                      rel="noreferrer"
                    />
                  ),
                }}
              />
            </div>
          }
          icon={
            <Icon
              type="user-music"
              size="4x"
              variant="light"
              aria-hidden
              className="text-default"
            />
          }
        />
      </div>
    );
  }

  if (isLoadingArtist || !artistData) {
    return (
      <div className="flex w-full items-center justify-center p-4xl">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{artistData.name}</title>
      </Helmet>
      <ScrollCollapseProvider scrollRef={scrollRef}>
        <div
          ref={scrollRef}
          className="h-full overflow-y-auto overscroll-contain [overflow-anchor:none]"
        >
          <Outlet context={{ artistData, scrollUpCallback }} />
        </div>
      </ScrollCollapseProvider>
    </>
  );
}

ArtistDetailsPage.displayName = 'ArtistDetailsPage';
